import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '@/stores/auth.store';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 20_000,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<ApiError>) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    return Promise.reject(err);
  },
);

// ---------- Shared types ----------
export interface ApiOk<T> {
  ok: true;
  data: T;
}

export interface ApiError {
  ok: false;
  error: { code: string; message: string; details?: unknown };
}

export function unwrap<T>(envelope: ApiOk<T> | T): T {
  if (envelope && typeof envelope === 'object' && 'ok' in envelope && (envelope as ApiOk<T>).ok) {
    return (envelope as ApiOk<T>).data;
  }
  return envelope as T;
}

export function extractErrorMessage(err: unknown, fallback = 'Xatolik yuz berdi'): string {
  const ax = err as AxiosError<ApiError>;
  return ax?.response?.data?.error?.message ?? ax?.message ?? fallback;
}

/** Pull the structured `{ code, message, details }` envelope from an axios
 *  error so callers can render specific UI per error code instead of just
 *  showing the message string. */
export function extractApiError(err: unknown): {
  code: string;
  message: string;
  details?: Record<string, unknown>;
} {
  const ax = err as AxiosError<ApiError>;
  return {
    code: ax?.response?.data?.error?.code ?? '',
    message: ax?.response?.data?.error?.message ?? ax?.message ?? '',
    details: (ax?.response?.data?.error?.details as Record<string, unknown>) ?? undefined,
  };
}
